/**
 * Cache Invalidation Helpers for Waritto
 *
 * Provides type-safe, consistent cache invalidation across the application.
 * Eliminates duplicated invalidation logic in mutation hooks.
 */

import type { QueryClient } from "@tanstack/react-query";
import { groupKeys, sessionKeys, expenseKeys, paymentKeys, balanceKeys } from "./queryKeys";

// ==================== Group Invalidation ====================

/**
 * Invalidate all group-related queries
 */
export function invalidateGroupQueries(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: groupKeys.all });
}

/**
 * Invalidate a specific group and its related queries
 */
export function invalidateGroupDetail(queryClient: QueryClient, groupId: string) {
  queryClient.invalidateQueries({ queryKey: groupKeys.detail(groupId) });
  queryClient.invalidateQueries({ queryKey: groupKeys.members(groupId) });
}

/**
 * Invalidate group members
 */
export function invalidateGroupMembers(queryClient: QueryClient, groupId: string) {
  queryClient.invalidateQueries({ queryKey: groupKeys.members(groupId) });
}

// ==================== Session Invalidation ====================

/**
 * Invalidate session queries for a group
 */
export function invalidateSessionQueries(queryClient: QueryClient, groupId: string) {
  queryClient.invalidateQueries({ queryKey: sessionKeys.group(groupId) });
}

/**
 * Invalidate a specific session and its related queries
 */
export function invalidateSessionDetail(
  queryClient: QueryClient,
  groupId: string,
  sessionId: string
) {
  queryClient.invalidateQueries({ queryKey: sessionKeys.detail(groupId, sessionId) });
  queryClient.invalidateQueries({ queryKey: sessionKeys.images(groupId, sessionId) });
}

// ==================== Expense Invalidation ====================

/**
 * Invalidate all expense-related queries for a session
 * This is the common pattern used after creating, updating, or deleting expenses
 */
export function invalidateExpenseQueries(
  queryClient: QueryClient,
  groupId: string,
  sessionId: string
) {
  // Invalidate expense list and detail
  queryClient.invalidateQueries({ queryKey: expenseKeys.session(groupId, sessionId) });

  // Invalidate balances (affected by expense changes)
  queryClient.invalidateQueries({ queryKey: expenseKeys.balances(groupId, sessionId) });

  // Invalidate settlements (affected by expense changes)
  queryClient.invalidateQueries({ queryKey: expenseKeys.settlements(groupId, sessionId) });

  // Invalidate settlements with payments
  queryClient.invalidateQueries({
    queryKey: paymentKeys.settlementsWithPayments(groupId, sessionId),
  });

  // Invalidate global balance summary
  queryClient.invalidateQueries({ queryKey: balanceKeys.summary });
}

// ==================== Payment Invalidation ====================

/**
 * Invalidate payment-related queries for a session
 */
export function invalidatePaymentQueries(
  queryClient: QueryClient,
  groupId: string,
  sessionId: string
) {
  queryClient.invalidateQueries({ queryKey: paymentKeys.session(groupId, sessionId) });
  queryClient.invalidateQueries({
    queryKey: paymentKeys.settlementsWithPayments(groupId, sessionId),
  });

  // Payments affect global balance
  queryClient.invalidateQueries({ queryKey: balanceKeys.summary });
}

// ==================== Balance Invalidation ====================

/**
 * Invalidate balance summary
 */
export function invalidateBalanceSummary(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: balanceKeys.summary });
}
